import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Ocorreu um erro. Tenta novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || "").join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && user.email) nav("/admin", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo de volta!");
      // navigation handled by the effect above once `user` is set (avoids double-nav race)
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-muted relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-secondary/20 blur-3xl" />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        data-testid="admin-login-form"
        className="relative w-full max-w-md bg-card rounded-[2rem] border border-border shadow-xl p-8 md:p-10"
      >
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo3duo.png" alt="3Duo" className="h-8 w-auto" />
          <span className="font-display font-extrabold text-xl">Admin</span>
        </div>
        <h1 className="font-display font-black text-2xl tracking-tight flex items-center gap-2"><Lock size={20} /> Área reservada</h1>
        <p className="text-muted-foreground text-sm mt-2">Inicia sessão para gerir produtos e categorias.</p>

        {/* The placeholder used to spell out the real administrator address,
            which handed half the credentials to anyone opening the page. */}
        <label className="block text-sm font-semibold mt-6 mb-1">Email</label>
        <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          autoComplete="username" autoCapitalize="none" spellCheck={false}
          className="w-full rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" placeholder="Endereço de email" />

        <label className="block text-sm font-semibold mt-4 mb-1">Palavra-passe</label>
        <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          autoComplete="current-password"
          className="w-full rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" placeholder="••••••••" />

        <button data-testid="login-submit" disabled={loading}
          className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full brand-gradient text-white font-semibold py-3.5 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60">
          {loading ? "A entrar..." : <>Entrar <ArrowRight size={17} /></>}
        </button>
      </motion.form>
    </div>
  );
}
